/* eslint-disable no-undef */
class CarouselComponent extends HTMLElement {
  constructor () {
    super()
    this.handleResize = debounce(this.handleResize.bind(this), 200)
    this.plugins = []
    this.prevBtnHandler = null
    this.nextBtnHandler = null
    this.lastKnownWidth = window.innerWidth
    this.carouselControls = this.querySelector('.carousel-controls')
    this.hasAnimatedNext = false
    this.intersectionObserver = null
  }

  connectedCallback () {
    this.initCarousel()
    window.addEventListener('resize', this.handleResize)
    this.updateControlsVisibility()

    // Add intersection observer if animation is enabled
    if (this.animateNextOnShow) {
      this.setupIntersectionObserver()
    }
  }

  disconnectedCallback () {
    window.removeEventListener('resize', this.handleResize)
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
    this.destroyCarousel()
  }

  initCarousel () {
    if (!this.carousel || !this.isCarouselActive()) {
      console.log('CarouselComponent: carousel or options not active for this viewport', this.carousel, this.isCarouselActive())
      this.updateControlsVisibility()
      return
    }

    if (this.pluginType !== 'none') {
      this.loadPluginsAndInit()
      return
    }
    this.setupCarousel()
  }

  loadPluginsAndInit () {
    const pluginPromises = []

    if (this.shouldInitPlugin()) {
      if (this.pluginType === 'autoscroll') {
        pluginPromises.push(
          new Promise(resolve => {
            window.Tentbox.loadScript(window.Tentbox.carousel.plugins.autoscrollSrc, resolve)
          }),
        )
      }

      if (this.pluginType === 'autoplay') {
        pluginPromises.push(
          new Promise(resolve => {
            window.Tentbox.loadScript(window.Tentbox.carousel.plugins.autoplaySrc, resolve)
          }),
        )
      }
    }

    Promise.all(pluginPromises).then(() => {
      this.setupCarousel()
    })
  }

  setupCarousel () {
    if (this.embla) {
      this.destroyCarousel()
    }

    const options = this.options

    if (this.pluginType === 'none') {
      this.embla = EmblaCarousel(this.carousel, options)
    }

    if (this.hasControls) {
      const prevBtn = this.querySelector('.carousel-controls__button--previous')
      const nextBtn = this.querySelector('.carousel-controls__button--next')

      // Remove existing event listeners if they exist
      if (this.prevBtnHandler) {
        prevBtn.removeEventListener('click', this.prevBtnHandler)
      }
      if (this.nextBtnHandler) {
        nextBtn.removeEventListener('click', this.nextBtnHandler)
      }

      // Define new event handler functions
      this.prevBtnHandler = () => this.embla.scrollPrev({ jump: true })
      this.nextBtnHandler = () => this.embla.scrollNext({ jump: true })

      // Add new event listeners
      prevBtn.addEventListener('click', this.prevBtnHandler, false)
      nextBtn.addEventListener('click', this.nextBtnHandler, false)
    }

    if (this.pluginType !== 'none') {
      const plugins = []

      if (this.shouldInitPlugin()) {
        if (this.pluginType === 'autoscroll') {
          plugins.push(EmblaCarouselAutoScroll({ speed: this.pluginSpeedForAutoscroll }))
          this.addMouseEventsForAutoscroll()
        }

        if (this.pluginType === 'autoplay') {
          plugins.push(EmblaCarouselAutoplay({ delay: this.pluginSpeedForAutoplay, playOnInit: true }))
          this.addMouseEventsForAutoplay()
        }
      }

      this.embla = EmblaCarousel(this.carousel, options, plugins)
      this.plugins = plugins
    }

    if (this.hasDots) {
      this.embla.on('select', () => {
        this.updateDots()
      })

      this.setupDots()
    }
  }

  destroyCarousel () {
    if (this.embla) {
      this.plugins.forEach(plugin => {
        if (plugin && typeof plugin.destroy === 'function') {
          plugin.destroy()
        }
      })
      this.embla.destroy()
    }
  }

  addMouseEventsForAutoscroll () {
    this.carousel.addEventListener('mouseenter', () => {
      this.embla.plugins().autoScroll.stop()
    })

    this.carousel.addEventListener('mouseleave', () => {
      this.embla.plugins().autoScroll.play()
    })
  }

  addMouseEventsForAutoplay () {
    const mouseTarget = this.hasDots ? this : this.carousel
    mouseTarget.addEventListener('mouseenter', () => {
      this.embla.plugins().autoplay.stop()
    })

    mouseTarget.addEventListener('mouseleave', () => {
      this.embla.plugins().autoplay.play()
    })
  }

  handleResize () {
    const currentWidth = window.innerWidth

    if (this.lastKnownWidth !== currentWidth) {
      this.lastKnownWidth = currentWidth

      const shouldInitCarousel = this.isCarouselActive()

      if (!this.embla && shouldInitCarousel) {
        this.initCarousel()
      } else if (this.embla && !shouldInitCarousel) {
        this.destroyCarousel()
      } else if (this.embla && this.shouldInitPlugin()) {
        this.loadPluginsAndInit()
      }

      this.updateControlsVisibility()
    }
  }

  setupDots () {
    const dotsContainer = this.dotsContainer
    dotsContainer.innerHTML = ''
    const slidesPerScroll = this.options.slidesToScroll || 1

    const slides = slidesPerScroll === 1 ? this.embla.slideNodes().length : Math.ceil(this.embla.slideNodes().length / slidesPerScroll)

    for (let i = 0; i < slides; i++) {
      const dot = document.createElement('button')
      dot.setAttribute('type', 'button')
      dot.classList.add('embla__dot', 'rounded-full', 'w-2', 'h-2', 'bg-steel', 'border-0', 'p-0', 'cursor-pointer')
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`)
      dot.addEventListener('click', () => {
        this.embla.scrollTo(i)
        this.updateDots()
      })
      dotsContainer.appendChild(dot)
    }

    this.updateDots() // Initial update for correct dot highlighting
  }

  updateDots () {
    const dots = this.dotsContainer.children
    const selectedIndex = this.embla.selectedScrollSnap()
    for (let i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('bg-oil', i === selectedIndex)
      dots[i].classList.toggle('bg-steel', i !== selectedIndex)
    }
  }

  isCarouselActive () {
    const breakpoints = this.options.breakpoints || {}
    const viewportWidth = window.innerWidth
    let isActive = this.options.active !== false // Default to true unless explicitly set to false

    // Sort breakpoints in descending order
    const sortedBreakpoints = Object.entries(breakpoints).sort((a, b) => {
      const widthA = parseInt(a[0].match(/\d+/)[0], 10)
      const widthB = parseInt(b[0].match(/\d+/)[0], 10)
      return widthB - widthA
    })

    for (const [breakpoint, settings] of sortedBreakpoints) {
      const minWidth = parseInt(breakpoint.match(/\d+/)[0], 10)
      if (viewportWidth >= minWidth) {
        isActive = settings.active !== false
        break // Exit the loop after finding the first matching breakpoint
      }
    }

    return isActive
  }

  shouldInitPlugin () {
    const pluginFromBreakpoint = this.pluginFrom || 'all'
    const viewportWidth = window.innerWidth
    const breakpoints = {
      xs: 450,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    }

    if (pluginFromBreakpoint === 'all') {
      return true
    }

    const minWidth = breakpoints[pluginFromBreakpoint]
    return viewportWidth >= minWidth
  }

  get carousel () {
    return this.querySelector('[carousel]')
  }

  get options () {
    try {
      return this.dataset.options ? JSON.parse(this.dataset.options) : {}
    } catch (e) {
      console.error('CarouselComponent: Invalid options format', e)
      return {}
    }
  }

  get dotsContainer () {
    return this.querySelector('.embla__dots')
  }

  get hasDots () {
    return this.dotsContainer !== null
  }

  get hasControls () {
    return this.querySelector('.carousel-controls') !== null
  }

  get pluginType () {
    return this.dataset.plugin || 'none'
  }

  get pluginFrom () {
    return this.dataset.pluginFrom || 'all' // Reads 'data-plugin-from' value
  }

  get pluginSpeedForAutoscroll () {
    const speed = this.dataset.pluginSpeed || 'slow'
    const speeds = {
      slow: 2,
      medium: 4,
      fast: 6,
    }
    return speeds[speed] || speeds.slow
  }

  get pluginSpeedForAutoplay () {
    const delay = this.dataset.pluginSpeed || 'medium'
    const delays = {
      slow: 5000, // 5 seconds
      medium: 3000, // 3 seconds
      fast: 1000, // 1 second
    }
    return delays[delay] || delays.medium
  }

  get animateNextOnShow () {
    return this.dataset.animateNextOnShow === 'true'
  }

  updateControlsVisibility () {
    if (this.carouselControls) {
      if (this.isCarouselActive()) {
        this.carouselControls.style.opacity = '1'
      } else {
        this.carouselControls.style.opacity = '0'
      }
    }
  }

  setupIntersectionObserver () {
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimatedNext && this.embla) {
          setTimeout(() => {
            this.embla.scrollNext()
            this.hasAnimatedNext = true
          }, 1500)
          this.intersectionObserver.disconnect()
        }
      })
    })

    this.intersectionObserver.observe(this)
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent)
}
